import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from "react";
import { CreditSelectTabState } from "../index";
import { useSearchParams } from "react-router-dom";

// 페이지네이션 및 정렬 타입 정의
interface TabPaginationContext {
  pagination: { page: number; pageSize: number };
  onPagination: (param: { page: number; pageSize: number }) => void;
  sort?: string;
  onSortChange: (sort: string | undefined) => void;
  order?: "ASC" | "DESC";
  onOrderChange: (order: "ASC" | "DESC" | undefined) => void;
}

// 전체 탭 컨텍스트 상태 타입
interface CreditTabContextState {
  usageHistory: TabPaginationContext;
  purchaseRefund: TabPaginationContext;
  expirationScheduled: TabPaginationContext;
}

// 컨텍스트 타입 정의 (currentTab 제거)
interface CreditContextType {
  tabContext: CreditTabContextState;
}

// 컨텍스트 생성
const CreditContext = createContext<CreditContextType | undefined>(undefined);

// 컨텍스트 프로바이더 컴포넌트
export const CreditContextProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();

  // URL에서 초기값 가져오기
  const getInitialPagination = (tabPrefix: string) => {
    const urlPage = parseInt(searchParams.get(`${tabPrefix}Page`) || "1", 10);
    const page = Math.max(urlPage, 1); // 최소값 1 보장
    const pageSize = parseInt(
      searchParams.get(`${tabPrefix}PageSize`) || "10",
      10
    );
    return { page, pageSize };
  };

  const getInitialSort = (tabPrefix: string) => {
    return searchParams.get(`${tabPrefix}Sort`) || undefined;
  };

  const getInitialOrder = (tabPrefix: string) => {
    const order = searchParams.get(`${tabPrefix}Order`);
    return order === "ASC" || order === "DESC" ? order : undefined;
  };

  // 탭 컨텍스트 상태 초기화
  const [tabContext, setTabContext] = useState<CreditTabContextState>({
    usageHistory: {
      pagination: getInitialPagination("usageHistory"),
      onPagination: (param) => {
        setTabContext((prev) => ({
          ...prev,
          usageHistory: {
            ...prev.usageHistory,
            pagination: param,
          },
        }));
      },
      sort: getInitialSort("usageHistory"),
      onSortChange: (sort) => {
        setTabContext((prev) => ({
          ...prev,
          usageHistory: {
            ...prev.usageHistory,
            sort,
          },
        }));
      },
      order: getInitialOrder("usageHistory"),
      onOrderChange: (order) => {
        setTabContext((prev) => ({
          ...prev,
          usageHistory: {
            ...prev.usageHistory,
            order,
          },
        }));
      },
    },
    purchaseRefund: {
      pagination: getInitialPagination("purchaseRefund"),
      onPagination: (param) => {
        setTabContext((prev) => ({
          ...prev,
          purchaseRefund: {
            ...prev.purchaseRefund,
            pagination: param,
          },
        }));
      },
      sort: getInitialSort("purchaseRefund"),
      onSortChange: (sort) => {
        setTabContext((prev) => ({
          ...prev,
          purchaseRefund: {
            ...prev.purchaseRefund,
            sort,
          },
        }));
      },
      order: getInitialOrder("purchaseRefund"),
      onOrderChange: (order) => {
        setTabContext((prev) => ({
          ...prev,
          purchaseRefund: {
            ...prev.purchaseRefund,
            order,
          },
        }));
      },
    },
    expirationScheduled: {
      pagination: getInitialPagination("expirationScheduled"),
      onPagination: (param) => {
        setTabContext((prev) => ({
          ...prev,
          expirationScheduled: {
            ...prev.expirationScheduled,
            pagination: param,
          },
        }));
      },
      sort: getInitialSort("expirationScheduled"),
      onSortChange: (sort) => {
        setTabContext((prev) => ({
          ...prev,
          expirationScheduled: {
            ...prev.expirationScheduled,
            sort,
          },
        }));
      },
      order: getInitialOrder("expirationScheduled"),
      onOrderChange: (order) => {
        setTabContext((prev) => ({
          ...prev,
          expirationScheduled: {
            ...prev.expirationScheduled,
            order,
          },
        }));
      },
    },
  });

  // URL 파라미터 동기화를 위한 useEffect
  useEffect(() => {
    const newParams = new URLSearchParams(searchParams);

    // 1. usageHistory 탭 파라미터 설정 (페이지네이션 + 정렬)
    newParams.set(
      "usageHistoryPage",
      tabContext.usageHistory.pagination.page.toString()
    );
    newParams.set(
      "usageHistoryPageSize",
      tabContext.usageHistory.pagination.pageSize.toString()
    );

    if (tabContext.usageHistory.sort) {
      newParams.set("usageHistorySort", tabContext.usageHistory.sort);
    } else {
      newParams.delete("usageHistorySort");
    }

    if (tabContext.usageHistory.order) {
      newParams.set("usageHistoryOrder", tabContext.usageHistory.order);
    } else {
      newParams.delete("usageHistoryOrder");
    }

    // 2. purchaseRefund 탭 파라미터 설정 (페이지네이션 + 정렬)
    newParams.set(
      "purchaseRefundPage",
      tabContext.purchaseRefund.pagination.page.toString()
    );
    newParams.set(
      "purchaseRefundPageSize",
      tabContext.purchaseRefund.pagination.pageSize.toString()
    );

    if (tabContext.purchaseRefund.sort) {
      newParams.set("purchaseRefundSort", tabContext.purchaseRefund.sort);
    } else {
      newParams.delete("purchaseRefundSort");
    }

    if (tabContext.purchaseRefund.order) {
      newParams.set("purchaseRefundOrder", tabContext.purchaseRefund.order);
    } else {
      newParams.delete("purchaseRefundOrder");
    }

    // 3. expirationScheduled 탭 파라미터 설정 (페이지네이션 + 정렬)
    newParams.set(
      "expirationScheduledPage",
      tabContext.expirationScheduled.pagination.page.toString()
    );
    newParams.set(
      "expirationScheduledPageSize",
      tabContext.expirationScheduled.pagination.pageSize.toString()
    );

    if (tabContext.expirationScheduled.sort) {
      newParams.set(
        "expirationScheduledSort",
        tabContext.expirationScheduled.sort
      );
    } else {
      newParams.delete("expirationScheduledSort");
    }

    if (tabContext.expirationScheduled.order) {
      newParams.set(
        "expirationScheduledOrder",
        tabContext.expirationScheduled.order
      );
    } else {
      newParams.delete("expirationScheduledOrder");
    }

    // URL 업데이트 (현재 URL과 다를 경우에만)
    const currentParams = new URLSearchParams(searchParams);
    if (newParams.toString() !== currentParams.toString()) {
      setSearchParams(newParams, { replace: true });
    }
  }, [tabContext, setSearchParams, searchParams]);

  return (
    <CreditContext.Provider value={{ tabContext }}>
      {children}
    </CreditContext.Provider>
  );
};

// 커스텀 훅으로 컨텍스트 사용 간편화
export const useCreditContext = () => {
  const context = useContext(CreditContext);
  if (context === undefined) {
    throw new Error(
      "useCreditContext는 CreditContextProvider 내부에서만 사용할 수 있습니다"
    );
  }
  return context;
};

// 특정 탭의 컨텍스트만 가져오는 함수
export const useTabContext = (tab: CreditSelectTabState) => {
  const { tabContext } = useCreditContext();

  switch (tab) {
    case CreditSelectTabState.USAGE_HISTORY:
      return tabContext.usageHistory;
    case CreditSelectTabState.PURCHASE_REFUND:
      return tabContext.purchaseRefund;
    case CreditSelectTabState.EXPIRATION_SCHEDULED:
      return tabContext.expirationScheduled;
    default:
      return tabContext.usageHistory;
  }
};
